/**
 * สูตรคณิตศาสตร์ในข้อความ (D23) — ข้อความระหว่าง `$...$` เรนเดอร์ด้วย KaTeX
 *
 * จำเป็นเพราะ 2^8, (8/9)^5, a^{m+n}, x^2-y^2, \overline{AB}, \angle ABC,
 * m(\overline{ST}) เขียนด้วยยูนิโค้ดธรรมดาไม่ได้ — คณิต ม.1 (เลขยกกำลัง · ค21103)
 *
 * ★ self-host ทั้ง JS/CSS/ฟอนต์ ผ่าน npm + vite ไม่แตะ CDN (เหตุผลเดียวกับฟอนต์ไทย/จีน
 *   ใน CLAUDE.md §6 — latency จากไทยและความเสถียร) · ฟอนต์ KaTeX 60 ไฟล์ถูก bundle
 *   เป็น asset ของเราเอง
 * ★ `trust: false` ปิด \href \includegraphics \url และ macro ที่แหย่ DOM ได้
 *   `strict: true` ปิดพฤติกรรมหลวม ๆ ของ LaTeX ที่ทำให้ผลลัพธ์เดาไม่ได้
 *
 * กติกาการตัดคำ (สำคัญกว่าที่คิด เพราะเนื้อหาไทยมี `$` ปนได้):
 *   - `$` ที่ **ไม่มีคู่ปิด** แสดงเป็นตัวอักษร `$` ตามเดิม ไม่ใช่กลืนข้อความที่เหลือ
 *   - `\$` = `$` ธรรมดาเสมอ ไม่เริ่มสูตร (ทางออกเวลาต้องพิมพ์ราคา)
 *   - `$...$` ที่ว่างเปล่า (`$$`) ถือว่าไม่ใช่สูตร
 *   - สูตรที่ LaTeX ผิดไวยากรณ์ แสดงเป็นข้อความดิบพร้อม `$` ครอบ ไม่ใช่กล่องแดง
 *     — เด็กเห็นโจทย์ดิบยังเดาได้ เห็น "KaTeX parse error" ไม่ได้อะไรเลย
 *
 * ★ ข้อความที่ไม่มี `$` ต้องออกมาเป็น **สตริงเดิมตัวต่อตัว** (คลัง 1,699 ข้อก่อน D23
 *   ไม่มี `$` เลย จึงต้องไม่มีอะไรเปลี่ยน) — ตรึงไว้ใน `math.test.tsx`
 */
import katex from "katex";
import type { ReactNode } from "react";

const OPTIONS = {
  displayMode: false,
  throwOnError: true,
  trust: false,
  strict: true,
  output: "html",
} as const;

/** true เมื่อข้อความมีสูตรอย่างน้อยหนึ่งชุด — ใช้ข้ามงานทั้งหมดสำหรับข้อความธรรมดา */
export function hasMath(text: string): boolean {
  return text.includes("$");
}

function formula(tex: string, key: number): ReactNode {
  try {
    const html = katex.renderToString(tex, OPTIONS);
    return (
      <span
        key={key}
        className="math"
        data-testid="math"
        // KaTeX สร้าง markup นี้เองจาก TeX ที่ escape แล้ว และปิด trust ไว้
        // ตัวสตริงจากไฟล์เนื้อหาไม่เคยถูกใส่เป็น HTML ตรง ๆ
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  } catch {
    return `$${tex}$`;
  }
}

/**
 * แปลงข้อความเป็นชิ้นส่วนสำหรับ React — สตริงล้วนกับ `<span>` ของสูตรสลับกัน
 * ข้อความที่ไม่มีสูตรคืน `[text]` (สตริงเดิม ไม่ถูกแตะ)
 */
export function renderMath(text: string): ReactNode[] {
  if (!hasMath(text)) return [text];
  const out: ReactNode[] = [];
  let buffer = "";
  let i = 0;
  let key = 0;
  const flush = (): void => {
    if (buffer) out.push(buffer);
    buffer = "";
  };
  while (i < text.length) {
    const ch = text[i];
    if (ch === "\\" && text[i + 1] === "$") {
      buffer += "$";
      i += 2;
      continue;
    }
    if (ch !== "$") {
      buffer += ch;
      i += 1;
      continue;
    }
    // หาคู่ปิด — ข้าม \$ ระหว่างทาง
    let j = i + 1;
    while (j < text.length && !(text[j] === "$" && text[j - 1] !== "\\"))
      j += 1;
    const tex = text.slice(i + 1, j);
    if (j >= text.length || !tex.trim()) {
      buffer += "$"; // ไม่มีคู่ปิด หรือว่างเปล่า → เป็นตัวอักษรตามเดิม
      i += 1;
      continue;
    }
    flush();
    out.push(formula(tex, key));
    key += 1;
    i = j + 1;
  }
  flush();
  return out;
}
